import { api, ME_TOKEN, RecordPeek, useAuthStore } from '@bemor/web-kit';
import {
    AsOfBadge,
    Button,
    ConfirmDialog,
    DateTime,
    ObjectRef,
    PageHeader,
    ReasonDialog,
    StatusBadge,
    type ListColumnDef,
    type ListViewPreset,
} from '@bemor/web-kit/ui';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import type { components as adminApi } from '@/api/generated/admin';
import type { Approval } from '@/api/types';
import { DetailBody } from '@/components/detail';
import {
    approvalLabel,
    isStaffTotpReset,
    ownLocked,
    staffResetOf,
} from '@/lib/approvals';
import { BulkFailures, reportBulk, type BulkResult } from '@/lib/bulk';
import {
    AdminListPage,
    adminListPrefetch,
    filterDefs,
    useAdminList,
} from '@/lib/lists';
import { useOne, useWrite } from '@/lib/queries';
import { useMe, useStaffDirectory, whoName } from '@/lib/staff';

const PATH = '/staff/approvals';
const PAGE_KEY = 'approvals';

export const APPROVAL_ACTIONS = [
    'identity.merchants.approve',
    'identity.merchants.suspend',
    'payments.refunds.approve',
    'admin.flags.kill_switch',
    'identity.staff.manage',
] as const;

/** What waits for a decision first (the old default), then what I asked for, then everything. */
export const APPROVAL_PRESETS: ListViewPreset[] = [
    { key: 'pending', name: 'Pending', filters: { state: ['pending'] } },
    {
        key: 'mine',
        name: 'Requested by me',
        filters: { requested_by: [ME_TOKEN] },
    },
    { key: 'all', name: 'All requests', filters: {} },
];

/** The route's prefetch: the person's views, then the rows of the view they land on (BM-46 §2.0 + §2.4). */
export const approvalsPrefetch = adminListPrefetch(PAGE_KEY, PATH, {
    presets: APPROVAL_PRESETS,
});

const approvalFilters = filterDefs(PATH, {
    q: 'Search',
    state: 'State',
    action: 'Action',
    range: 'Requested',
});

type ApprovalsBulkRequest = adminApi['schemas']['ApprovalsBulkRequest'];

/** `POST /staff/approvals/bulk`: each id through the single decision (four eyes included), one result per id. */
export async function approvalsBulk(
    body: ApprovalsBulkRequest,
): Promise<BulkResult[]> {
    return (await api().post<BulkResult[]>('/staff/approvals/bulk', body)).data;
}

/**
 * Whether this person may decide the request: still pending, someone else asked (four eyes), and they hold the
 * permission the request names. The server decides anyway; this only keeps the buttons honest.
 */
export function mayDecide(
    a: Approval,
    me: string,
    can: (permission: string) => boolean,
): boolean {
    return a.state === 'pending' && !ownLocked(a, me) && can(a.action);
}

/** The peeked request, read on its own (`GET /staff/approvals/{id}`), with Grant and Reject when allowed. */
function ApprovalPeek({ id }: { id: string }) {
    const me = useMe();
    const can = useAuthStore((s) => s.can);
    const directory = useStaffDirectory();
    const query = useOne<Approval>(`/staff/approvals/${id}`);
    const [decision, setDecision] = useState<'grant' | 'reject' | null>(null);
    const decide = useWrite(
        async (body: { decision: 'grant' | 'reject'; reason: string }) =>
            (
                await api().post<Approval>(
                    `/staff/approvals/${id}/${body.decision}`,
                    { reason: body.reason || undefined },
                )
            ).data,
        ['/staff/approvals', '/staff/queues'],
    );
    return (
        <DetailBody query={query} title="Approval">
            {(a) => (
                <div className="flex flex-col gap-4">
                    <dl
                        className="grid grid-cols-1 gap-3 text-sm"
                        data-testid="approval-peek"
                    >
                        <div>
                            <dt className="text-muted-foreground">Request</dt>
                            <dd className="font-medium">{a.summary}</dd>
                        </div>
                        <div>
                            <dt className="text-muted-foreground">Action</dt>
                            <dd>
                                {approvalLabel(a)}{' '}
                                <span className="text-fg-2 font-mono text-xs">
                                    {a.action}
                                </span>
                            </dd>
                        </div>
                        <div>
                            <dt className="text-muted-foreground">State</dt>
                            <dd>
                                <StatusBadge
                                    status={a.state}
                                    domain="approval"
                                />
                            </dd>
                        </div>
                        <div>
                            <dt className="text-muted-foreground">Merchant</dt>
                            <dd>
                                {a.merchant_id ? (
                                    <ObjectRef id={a.merchant_id} />
                                ) : (
                                    '—'
                                )}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-muted-foreground">
                                Requested by
                            </dt>
                            <dd>
                                {whoName(a.requested_by, me, directory)},{' '}
                                <DateTime value={a.created_at} empty="—" />
                            </dd>
                        </div>
                        <div>
                            <dt className="text-muted-foreground">
                                {a.decided_by ? 'Decided by' : 'Expires'}
                            </dt>
                            <dd>
                                {a.decided_by ? (
                                    <>
                                        {whoName(a.decided_by, me, directory)}
                                        {a.reason ? `: ${a.reason}` : null}
                                    </>
                                ) : (
                                    <DateTime value={a.expires_at} empty="—" />
                                )}
                            </dd>
                        </div>
                        <div>
                            <dt className="text-muted-foreground">
                                What the owning service will execute
                            </dt>
                            <dd>
                                <pre className="bg-muted overflow-x-auto rounded p-2 text-xs">
                                    {JSON.stringify(a.payload, null, 2)}
                                </pre>
                            </dd>
                        </div>
                    </dl>
                    {ownLocked(a, me) ? (
                        <p
                            className="text-fg-2 m-0 text-sm"
                            data-testid="peek-four-eyes"
                        >
                            You asked for this one: someone else has to decide
                            it.
                        </p>
                    ) : null}
                    {mayDecide(a, me, can) ? (
                        <div
                            className="flex gap-2"
                            data-testid="approval-peek-actions"
                        >
                            <Button
                                size="sm"
                                onClick={() => setDecision('grant')}
                            >
                                Grant
                            </Button>
                            <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => setDecision('reject')}
                            >
                                Reject
                            </Button>
                        </div>
                    ) : null}
                    {decision ? (
                        <ReasonDialog
                            open
                            onOpenChange={(o) => (o ? null : setDecision(null))}
                            title={
                                decision === 'grant'
                                    ? 'Grant this request'
                                    : 'Reject this request'
                            }
                            confirmLabel={
                                decision === 'grant' ? 'Grant' : 'Reject'
                            }
                            destructive={decision === 'reject'}
                            required={decision === 'reject'}
                            busy={decide.isPending}
                            error={decide.error}
                            onConfirm={(reason) =>
                                decide.mutate(
                                    { decision, reason },
                                    { onSuccess: () => setDecision(null) },
                                )
                            }
                        />
                    ) : null}
                </div>
            )}
        </DetailBody>
    );
}

export function ApprovalsPage() {
    const me = useMe();
    const directory = useStaffDirectory();
    const columns: ListColumnDef<Approval>[] = [
        {
            key: 'summary',
            label: 'Request',
            cell: (a) => (
                <Link
                    to={`/approvals/${a.id}`}
                    className="hover:underline"
                    onClick={(e) => e.stopPropagation()}
                >
                    {a.summary}
                </Link>
            ),
            locked: 'start',
            boardCard: true,
        },
        {
            key: 'action',
            label: 'Action',
            sortKey: 'action',
            cell: (a) => (
                <span className="flex min-w-0 flex-col">
                    <span className="truncate">{approvalLabel(a)}</span>
                    <span className="text-fg-2 truncate font-mono text-xs">
                        {a.action}
                    </span>
                </span>
            ),
            groupable: { key: 'action', value: (a) => a.action },
            boardCard: true,
        },
        {
            key: 'subject',
            label: 'Subject',
            cell: (a) => {
                if (isStaffTotpReset(a)) {
                    const m = directory.byStaffId(staffResetOf(a).staffId);
                    if (m)
                        return (
                            <span className="flex min-w-0 flex-col">
                                <span className="truncate">{m.user.name}</span>
                                <span className="text-fg-2 truncate text-xs">
                                    {m.user.email}
                                </span>
                            </span>
                        );
                }
                return <ObjectRef id={a.subject_id} />;
            },
        },
        {
            key: 'state',
            label: 'State',
            sortKey: 'state',
            cell: (a) => <StatusBadge status={a.state} domain="approval" />,
            groupable: { key: 'state', statusDomain: 'approval' },
        },
        {
            key: 'requested_by',
            aliases: ['requested'],
            label: 'Requested by',
            sortKey: 'requested_by',
            cell: (a) =>
                a.requested_by === me ? (
                    <span className="font-medium">you</span>
                ) : (
                    whoName(a.requested_by, me, directory)
                ),
        },
        {
            key: 'decided_by',
            aliases: ['decided'],
            label: 'Decided by',
            sortKey: 'decided_by',
            cell: (a) => whoName(a.decided_by, me, directory),
        },
        {
            key: 'expires_at',
            aliases: ['expires'],
            label: 'Expires',
            kind: 'datetime',
            sortKey: 'expires_at',
            cell: (a) => <DateTime value={a.expires_at} empty="—" />,
            noTruncate: true,
        },
        {
            key: 'created_at',
            label: 'Requested',
            kind: 'datetime',
            sortKey: 'created_at',
            cell: (a) => <DateTime value={a.created_at} empty="—" />,
            noTruncate: true,
            defaultHidden: true,
        },
    ];
    const { state, query } = useAdminList<Approval>(PAGE_KEY, PATH, {
        columns,
        filters: approvalFilters,
        presets: APPROVAL_PRESETS,
    });
    const peeked = query.rows.find((a) => a.id === state.peek);
    const can = useAuthStore((s) => s.can);
    const [selected, setSelected] = useState<string[]>([]);
    const [failures, setFailures] = useState<BulkResult[]>([]);
    const [rejecting, setRejecting] = useState(false);
    const [granting, setGranting] = useState(false);
    const bulk = useWrite(approvalsBulk, ['/staff/approvals', '/staff/queues']);
    // Only the requests this person may decide are sent; the others stay selected and are named in the bar.
    const decidable = selected.filter((id) => {
        const a = query.rows.find((r) => r.id === id);
        return a ? mayDecide(a, me, can) : true;
    });
    const skipped = selected.length - decidable.length;
    const subjects = decidable.map(
        (id) => query.rows.find((r) => r.id === id)?.summary ?? id,
    );
    const run = (body: Omit<ApprovalsBulkRequest, 'ids'>, verb: string) =>
        bulk.mutate(
            { ...body, ids: decidable },
            {
                onSuccess: (results) => {
                    setRejecting(false);
                    const failed = reportBulk(results, verb, (ids) =>
                        setSelected([
                            ...ids,
                            ...selected.filter((id) => !decidable.includes(id)),
                        ]),
                    );
                    setFailures(failed);
                },
            },
        );
    return (
        <div>
            <PageHeader
                title="Approvals"
                meta={
                    <span className="flex flex-col gap-1">
                        <span>
                            {
                                'Four eyes: the person who asked can never be the one who grants. Undecided requests expire after 72 hours.'
                            }
                        </span>
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </span>
                }
            />
            <AdminListPage
                noun="approvals"
                state={state}
                query={query}
                rowKey={(a) => a.id}
                onRow={(a) => state.setPeek(a.id)}
                selection={
                    APPROVAL_ACTIONS.some((p) => can(p))
                        ? {
                              selected,
                              onChange: setSelected,
                              label: (a) => a.summary,
                              // BM-66 (design 0.6.0-a): your own pending request cannot be picked for a bulk decision
                              rowSelectable: (a) =>
                                  ownLocked(a, me)
                                      ? {
                                            ok: false,
                                            reason: 'Your own request: a second person decides it',
                                        }
                                      : { ok: true },
                          }
                        : undefined
                }
                bulk={{
                    noun: { one: 'request', other: 'requests' },
                    actions: (
                        <>
                            {skipped > 0 ? (
                                <span
                                    className="text-fg-2 text-xs whitespace-nowrap"
                                    data-testid="bulk-skipped"
                                >
                                    {skipped === 1
                                        ? '1 you cannot decide'
                                        : `${skipped} you cannot decide`}
                                </span>
                            ) : null}
                            {decidable.length > 0 ? (
                                <>
                                    <Button
                                        size="sm"
                                        disabled={bulk.isPending}
                                        onClick={() => setGranting(true)}
                                    >
                                        Grant {decidable.length}
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="destructive"
                                        disabled={bulk.isPending}
                                        onClick={() => setRejecting(true)}
                                    >
                                        Reject {decidable.length}
                                    </Button>
                                </>
                            ) : null}
                        </>
                    ),
                }}
                controls={{
                    q: { kind: 'search' },
                    state: { domain: 'approval' },
                    action: { options: APPROVAL_ACTIONS },
                }}
            />
            {/* BM-66 (design 0.6.0-a): a bulk confirm lists every request it acts on and says how many */}
            <ConfirmDialog
                open={granting}
                onCancel={() => setGranting(false)}
                onConfirm={() => {
                    setGranting(false);
                    run({ action: 'grant' }, 'granted');
                }}
                title={`Grant ${decidable.length === 1 ? 'this request' : `${decidable.length} requests`}?`}
                body="Each one is executed at once; a grant cannot be reversed, only countered."
                confirmLabel={`Grant ${decidable.length}`}
                subjects={subjects}
                loading={bulk.isPending}
            />
            {rejecting ? (
                <ReasonDialog
                    open
                    onOpenChange={(o) => (o ? null : setRejecting(false))}
                    title={`Reject ${decidable.length === 1 ? 'one request' : `${decidable.length} requests`}`}
                    description={
                        <span className="flex flex-col gap-2">
                            <span>
                                Each requester is told why; nothing is executed.
                            </span>
                            <ul
                                className="m-0 list-disc pl-5"
                                data-slot="confirm-subjects"
                            >
                                {subjects.map((s, i) => (
                                    <li key={i}>{s}</li>
                                ))}
                            </ul>
                        </span>
                    }
                    confirmLabel="Reject"
                    destructive
                    required
                    busy={bulk.isPending}
                    error={bulk.error}
                    onConfirm={(reason) =>
                        run({ action: 'reject', reason }, 'rejected')
                    }
                />
            ) : null}
            <BulkFailures
                failures={failures}
                labelOf={(id) =>
                    query.rows.find((a) => a.id === id)?.summary ?? id
                }
                onClose={() => setFailures([])}
            />
            <RecordPeek
                state={state}
                title={peeked?.summary ?? 'Approval'}
                href={(id) => `/approvals/${id}`}
            >
                {(id) => <ApprovalPeek id={id} />}
            </RecordPeek>
        </div>
    );
}
